import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Divider,
  Alert,
  IconButton,
  Collapse,
  styled,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { useWorkspace } from "../../utils/WorkspaceContext";
import { useView } from "../../utils/PersonalViewProvider";
import StatusChip from "../../utils/StatusChip";
import api from "../../services/api";

const StyledPaper = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(0.1),
  padding: theme.spacing(1),
}));

const TrackHeaderRow = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  alignItems: "center",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TaskHeaderRow = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1),
  marginLeft: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  fontWeight: "bold",
  alignItems: "center",
}));

const TaskRow = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1),
  marginLeft: theme.spacing(3),
  alignItems: "center",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const AssigneeBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  backgroundColor: theme.palette.background.main,
  borderRadius: theme.spacing(0.5),
  border: `1px solid ${theme.palette.grey[300]}`,
  fontSize: "0.875rem",
  color: theme.palette.text.primary,
  minHeight: "24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  maxWidth: "120px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const ColorBar = styled(Box)(({ color }) => ({
  width: "4px",
  height: "100%",
  backgroundColor: color,
  borderRadius: "2px",
  marginRight: "8px",
}));

const CompletionReport = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTracks, setExpandedTracks] = useState({});
  const { currentWorkspace } = useWorkspace();
  const { selectedAssignee } = useView();

  useEffect(() => {
    setLoading(true);
    const fetchReport = async () => {
      if (!currentWorkspace?.id) {
        setTracks([]);
        setLoading(false);
        return;
      }
      try {
        const params = {
          workspace_id: currentWorkspace.id,
          ...(selectedAssignee ? { assignee: selectedAssignee } : {}),
        };
        const response = await api.get("completion-report/", { params });
        setTracks(response.data.payload?.tracks || []);
      } catch (error) {
        setTracks([]);
      }
      setLoading(false);
    };
    fetchReport();
  }, [currentWorkspace, selectedAssignee]);

  const toggleTrack = (trackId) => {
    setExpandedTracks((prev) => ({
      ...prev,
      [trackId]: !prev[trackId],
    }));
  };

  if (loading) return <div>Loading...</div>;

  if (!tracks.length) {
    return <Alert severity="info">No data available</Alert>;
  }

  return (
    <Box sx={{ padding: 2 }}>
      {tracks.map((track) => (
        <StyledPaper key={track.id}>
          <TrackHeaderRow
            container
            spacing={2}
            onClick={() => toggleTrack(track.id)}
          >
            <Grid size={0.5}>
              <IconButton size="small">
                {expandedTracks[track.id] ? (
                  <KeyboardArrowDown />
                ) : (
                  <KeyboardArrowRight />
                )}
              </IconButton>
            </Grid>
            <Grid size={4}>
              <Typography variant="h6">
                {track.name || "Unnamed Track"}
              </Typography>
            </Grid>
            <Grid size={2}>
              <AssigneeBox>{track.assignee || "Unassigned"}</AssigneeBox>
            </Grid>
            <Grid size={2}>
              <Typography variant="body2">
                Tasks: {track.completed_tasks || 0}/{track.total_tasks || 0}
              </Typography>
            </Grid>
            <Grid size={3.5}>
              <LinearProgress
                variant="determinate"
                value={
                  (track.total_tasks || 0) > 0
                    ? ((track.completed_tasks || 0) /
                        (track.total_tasks || 0)) *
                      100
                    : 0
                }
                sx={{ width: "100%", height: 8, borderRadius: 4 }}
              />
            </Grid>
          </TrackHeaderRow>

          <Collapse
            in={!!expandedTracks[track.id]}
            timeout="auto"
            unmountOnExit
          >
            <Divider sx={{ mb: 2 }} />
            {!track.tasks || track.tasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No tasks in this track.
              </Typography>
            ) : (
              <>
                <TaskHeaderRow container spacing={2}>
                  <Grid size={4}>
                    <Typography variant="caption">Task Name</Typography>
                  </Grid>
                  <Grid size={1}>
                    <Typography variant="caption">Assignee</Typography>
                  </Grid>
                  <Grid size={1}>
                    <Typography variant="caption">Status</Typography>
                  </Grid>
                  <Grid size={1.5}>
                    <Typography variant="caption">Due Date</Typography>
                  </Grid>
                  <Grid size={1}>
                    <Typography variant="caption">Action Items</Typography>
                  </Grid>
                  <Grid size={1}>
                    <Typography variant="caption">Time Taken</Typography>
                  </Grid>
                  <Grid size={2}>
                    <Typography variant="caption">Progress</Typography>
                  </Grid>
                </TaskHeaderRow>

                {track.tasks.map((task) => (
                  <TaskRow key={task.id} container spacing={2}>
                    <Grid size={4}>
                      <Typography variant="subtitle1">
                        {task.name || "Unnamed Task"}
                      </Typography>
                    </Grid>{" "}
                    <Grid size={1}>
                      <AssigneeBox>{task.assignee || "Unassigned"}</AssigneeBox>
                    </Grid>
                    <Grid size={1}>
                      <StatusChip status={task.status || "To Start"} />
                    </Grid>
                    <Grid size={1.5}>
                      <Typography variant="body2">
                        {task.do_date || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid size={1}>
                      <Typography variant="body2">
                        {task.completed_action_items || 0}/
                        {task.total_action_items || 0}
                      </Typography>
                    </Grid>
                    <Grid size={1}>
                      <Typography variant="body2">
                        {task.time_taken || 0}
                      </Typography>
                    </Grid>
                    <Grid size={2}>
                      <LinearProgress
                        variant="determinate"
                        value={
                          (task.total_action_items || 0) > 0
                            ? ((task.completed_action_items || 0) /
                                (task.total_action_items || 0)) *
                              100
                            : 0
                        }
                        sx={{ width: "100%", height: 6, borderRadius: 3 }}
                      />
                    </Grid>
                  </TaskRow>
                ))}
              </>
            )}
          </Collapse>
        </StyledPaper>
      ))}
    </Box>
  );
};

export default CompletionReport;
